import { useState, useEffect, useRef, useCallback } from 'react';
import { PostChatService } from '../services/postChatService';
import { PostChatAudioRequest, PostChatMessage, PostContext, PostChatStatus } from '../types/chat';

interface UsePostChatOptions {
  contentPlanId: string;
  postId?: string;
  postContext: PostContext;
  onPostUpdate?: (updates: any) => void;
}

export const usePostChat = ({ contentPlanId, postId, postContext, onPostUpdate }: UsePostChatOptions) => {
  const [messages, setMessages] = useState<PostChatMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [chatStatus, setChatStatus] = useState<PostChatStatus>(PostChatStatus.Idle);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const chatServiceRef = useRef<PostChatService | null>(null);
  const onPostUpdateRef = useRef(onPostUpdate);
  const initializingRef = useRef(false); // Флаг для предотвращения множественных инициализаций
  const connectionAttemptRef = useRef(0); // Счетчик попыток подключения

  // Обновляем ref при изменении onPostUpdate
  useEffect(() => {
    onPostUpdateRef.current = onPostUpdate;
  }, [onPostUpdate]);

  // Единственный эффект для инициализации и управления чатом
  useEffect(() => {
    const initializeChat = async () => {
      // Проверяем условия инициализации
      if (!contentPlanId || initializingRef.current) {
        return;
      }
      
      // Защита от React StrictMode (двойной вызов эффектов)
      connectionAttemptRef.current += 1;
      const currentAttempt = connectionAttemptRef.current;
      
      initializingRef.current = true;
      
      try {
        setIsLoading(true);
        setError(null);
        
        
        // Отключаем предыдущий сервис если он существует
        if (chatServiceRef.current) {
          const oldService = chatServiceRef.current;
          chatServiceRef.current = null;
          // Отключаем асинхронно
          oldService.disconnect().catch(() => {});
        }
        
        // Очищаем состояние
        setMessages([]);
        setIsTyping(false);
        setChatStatus(PostChatStatus.Idle);
        setIsLoadingHistory(false);
        setIsConnected(false);
        
        // Создаем новый сервис
        const chatService = new PostChatService();
        chatServiceRef.current = chatService;

        // Настройка обработчиков событий
        chatService.onMessageReceived((message: PostChatMessage) => {
          setMessages(prev => [...prev, message]);
          
          if (message.isFromAI) {
            setIsTyping(false);
          }
        });

        chatService.onStatusChanged((statusEvent) => {
          const actualStatus = statusEvent.status !== undefined ? statusEvent.status : (statusEvent as any).Status;
          setChatStatus(actualStatus);
          
          // Проверяем статус как строку и как число для совместимости
          const isAITyping = actualStatus === PostChatStatus.AITyping || 
                           actualStatus === 1 || 
                           actualStatus === 'AITyping' ||
                           actualStatus === 'aiTyping';
          
          if (isAITyping) {
            setIsTyping(true);
          } else if (actualStatus === PostChatStatus.Idle || actualStatus === 0 || actualStatus === 'Idle') {
            setIsTyping(false);
          }
        });

        chatService.onError((errorMessage) => {
          setError(errorMessage);
          setIsTyping(false);
          setChatStatus(PostChatStatus.Idle);
        });

        chatService.onDisconnected(() => {
          setIsConnected(false);
        });

        chatService.onMessageDeleted((messageId) => {
          setMessages(prev => prev.filter(msg => msg.id !== messageId));
        });

        chatService.onChatCleared((data) => {
          const isMatchingContentPlan = data.contentPlanId === contentPlanId;
          const isMatchingPost = (data.postId === postId) || 
                                 (data.postId === undefined && postId === undefined) ||
                                 (data.postId === null && postId === null);
          
          if (isMatchingContentPlan && isMatchingPost) {
            setMessages([]);
            setIsTyping(false);
            setChatStatus(PostChatStatus.Idle);
          }
        });

        chatService.onReconnected(() => {
          setIsConnected(true);
        });

        // Подключаемся к SignalR Hub
        try {
          await chatService.connect();
          setIsConnected(true);
        } catch (connectError) {
          
          // Проверяем что это все еще актуальная попытка
          if (connectionAttemptRef.current !== currentAttempt) {
            return;
          }
          
          setIsConnected(false);
          
          // Различаем типы ошибок
          if (connectError instanceof Error) {
            if (connectError.message.includes('Failed to fetch') || 
                connectError.message.includes('ERR_CONNECTION_RESET') ||
                connectError.message.includes('ERR_NETWORK')) {
              setError('Нет подключения к интернету или сервер недоступен');
            } else if (connectError.message.includes('negotiate')) {
              setError('Ошибка согласования соединения с сервером');
            } else {
              setError('Не удалось подключиться к чату');
            }
          } else {
            setError('Неизвестная ошибка подключения');
          }
          
          throw connectError;
        }

        // Загружаем историю сообщений
        setIsLoadingHistory(true);
        
        try {
          // Проверяем что это все еще актуальная попытка
          if (connectionAttemptRef.current !== currentAttempt) {
            return;
          }
          
          const normalizedPostId = postId === undefined || postId === null || postId === "undefined" ? undefined : postId;
          
          const history = await chatService.getChatHistory(contentPlanId, normalizedPostId);
          
          // Проверяем что соединение все еще активно и попытка актуальна
          if (chatServiceRef.current === chatService && connectionAttemptRef.current === currentAttempt) {
            setMessages(history);
          }
          
        } catch (historyError) {
          // Проверяем что это все еще актуальная попытка
          if (chatServiceRef.current === chatService && connectionAttemptRef.current === currentAttempt) {
            // Не показываем ошибку истории если нет подключения
            if (isConnected) {
              setError('Не удалось загрузить историю чата');
            }
          }
        } finally {
          // Завершаем загрузку только для актуальной попытки
          if (connectionAttemptRef.current === currentAttempt) {
            setIsLoadingHistory(false);
          }
        }
        
      } catch (error) {
        setError('Не удалось подключиться к чату');
      } finally {
        setIsLoading(false);
        initializingRef.current = false;
      }
    };

    initializeChat();

    // Очистка при размонтировании или изменении зависимостей
    return () => {
      initializingRef.current = false;
      connectionAttemptRef.current = 0; // Сбрасываем счетчик попыток
      
      if (chatServiceRef.current) {
        chatServiceRef.current.disconnect().catch(() => {});
        chatServiceRef.current = null;
      }
    };
  }, [contentPlanId, postId]); // Убираем postContext из зависимостей

  // Отправка сообщения - добавляем актуальный postContext через ref
  const sendMessage = useCallback(async (message: string, imageUrls?: string[] | null) => {
    if (!message.trim() || !chatServiceRef.current || !isConnected || isTyping || isLoadingHistory) {
      return false;
    }

    try {
      setError(null);
      
      // Добавляем сообщение пользователя в список
      const userMessage: PostChatMessage = {
        id: Date.now().toString(),
        content: message.trim(),
        isFromAI: false,
        timestamp: new Date().toISOString(),
        isFromCurrentUser: true,
        imageUrls: imageUrls ?? null
      };
      setMessages(prev => [...prev, userMessage]);

      // Отправляем сообщение через SignalR, используя актуальный postContext
      await chatServiceRef.current.sendMessage(contentPlanId, postId, message.trim(), postContext, imageUrls ?? null);
      return true;
    } catch (error) {
      setError('Не удалось отправить сообщение');
      setIsTyping(false);
      setChatStatus(PostChatStatus.Idle);
      return false;
    }
  }, [contentPlanId, postId, postContext, isConnected, isTyping, isLoadingHistory]);

  // Регенерация ответа ИИ по конкретному AI-сообщению (без повторной отправки user-сообщения)
  const regenerateMessage = useCallback(async (aiMessageId: string) => {
    if (!aiMessageId || !chatServiceRef.current || !isConnected || isTyping || isLoadingHistory) {
      return false;
    }

    try {
      setError(null);
      setIsTyping(true);
      await chatServiceRef.current.regenerateMessage(aiMessageId, postContext);
      return true;
    } catch (error) {
      setError('Не удалось регенерировать сообщение');
      setIsTyping(false);
      setChatStatus(PostChatStatus.Idle);
      return false;
    }
  }, [postContext, isConnected, isTyping, isLoadingHistory]);

  const sendAudioMessage = useCallback(async (audio: Omit<PostChatAudioRequest, 'contentPlanId' | 'postId' | 'postContext'>) => {
    if (!chatServiceRef.current || !isConnected || isTyping || isLoadingHistory) {
      return false;
    }

    if (!audio.audioKey || !audio.audioUrl) {
      return false;
    }

    try {
      setError(null);

      const userMessage: PostChatMessage = {
        id: Date.now().toString(),
        content: '',
        messageType: 'audio',
        audioUrl: audio.audioUrl,
        durationMs: audio.durationMs,
        isFromAI: false,
        timestamp: new Date().toISOString(),
        isFromCurrentUser: true
      };
      setMessages(prev => [...prev, userMessage]);

      await chatServiceRef.current.sendAudioMessage(contentPlanId, postId, audio, postContext);
      return true;
    } catch (error) {
      setError('Не удалось отправить аудио');
      setIsTyping(false);
      setChatStatus(PostChatStatus.Idle);
      return false;
    }
  }, [contentPlanId, postId, postContext, isConnected, isTyping, isLoadingHistory]);

  // Удаление сообщения
  const deleteMessage = useCallback(async (messageId: string) => {
    if (!chatServiceRef.current || !isConnected) {
      return false;
    }

    try {
      setError(null);
      await chatServiceRef.current.deleteMessage(messageId);
      return true;
    } catch (error) {
      setError('Не удалось удалить сообщение');
      return false;
    }
  }, [isConnected]);

  // Очистка истории
  const clearHistory = useCallback(async () => {
    if (!chatServiceRef.current || !isConnected) {
      return false;
    }

    try {
      setError(null);
      await chatServiceRef.current.clearChatHistory(contentPlanId, postId);
      setMessages([]); // Немедленно очищаем сообщения в UI
      return true;
    } catch (error) {
      setError('Не удалось очистить историю');
      return false;
    }
  }, [contentPlanId, postId, isConnected]);

  // Очистка ошибки
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Обновление текста конкретного сообщения в истории на бэкенде
  const updateMessageContent = useCallback(async (messageId: string, nextContent: string) => {
    if (!messageId || !chatServiceRef.current || !isConnected) return false;
    
    // Сначала обновляем локально для мгновенного отклика
    setMessages((prev) => {
      const idx = prev.findIndex((m) => m.id === messageId);
      if (idx < 0) return prev;
      const existing = prev[idx];
      if (!existing) return prev;
      if ((existing.content || '') === (nextContent || '')) return prev;
      const updated: PostChatMessage = { ...existing, content: nextContent || '' };
      const copy = [...prev];
      copy[idx] = updated;
      return copy;
    });

    try {
      await chatServiceRef.current.updateMessageContent(messageId, nextContent);
      return true;
    } catch (error) {
      console.error('Failed to update message content on backend:', error);
      // При ошибке можно было бы откатить локальное изменение, 
      // но обычно пользователь просто увидит ошибку в консоли или уведомление
      return false;
    }
  }, [isConnected]);

  // Миграция сообщений с временного postId на реальный (используется при сохранении поста)
  const updatePostIdInMessages = useCallback(async (oldPostId: string, newPostId: string) => {
    if (!oldPostId || !newPostId) return false;
    if (!chatServiceRef.current || !isConnected) return false;
    try {
      await chatServiceRef.current.updatePostIdInMessages(contentPlanId, oldPostId, newPostId);
      return true;
    } catch {
      return false;
    }
  }, [contentPlanId, isConnected]);

  // Функция переподключения
  const reconnect = useCallback(async () => {
    if (initializingRef.current) {
      return false;
    }
    
    try {
      // Сбрасываем состояния ошибок
      setError(null);
      setIsLoading(true);
      setIsConnected(false);
      
      // Увеличиваем счетчик попыток для нового подключения
      connectionAttemptRef.current += 1;
      const currentAttempt = connectionAttemptRef.current;
      
      initializingRef.current = true;

      // Отключаем предыдущий сервис если есть
      if (chatServiceRef.current) {
        const oldService = chatServiceRef.current;
        chatServiceRef.current = null;
        oldService.disconnect().catch(() => {});
      }

      // Создаем новый сервис
      const chatService = new PostChatService();
      chatServiceRef.current = chatService;

      // Настройка обработчиков (упрощенная версия)
      chatService.onMessageReceived((message: PostChatMessage) => {
        setMessages(prev => [...prev, message]);
        if (message.isFromAI) setIsTyping(false);
      });

      chatService.onStatusChanged((statusEvent) => {
        const actualStatus = statusEvent.status !== undefined ? statusEvent.status : (statusEvent as any).Status;
        setChatStatus(actualStatus);
        
        // Проверяем статус как строку и как число для совместимости
        const isAITyping = actualStatus === PostChatStatus.AITyping || 
                         actualStatus === 1 || 
                         actualStatus === 'AITyping' ||
                         actualStatus === 'aiTyping';
        
        if (isAITyping) {
          setIsTyping(true);
        } else if (actualStatus === PostChatStatus.Idle || actualStatus === 0 || actualStatus === 'Idle') {
          setIsTyping(false);
        }
      });

      chatService.onError((errorMessage) => {
        setError(errorMessage);
        setIsTyping(false);
        setChatStatus(PostChatStatus.Idle);
      });

      chatService.onDisconnected(() => {
        setIsConnected(false);
      });

      chatService.onReconnected(() => {
        setIsConnected(true);
      });

      // Подключаемся
      await chatService.connect();
      
      // Проверяем актуальность попытки
      if (connectionAttemptRef.current !== currentAttempt) {
        return false;
      }
      
      setIsConnected(true);

      // Загружаем историю заново
      try {
        setIsLoadingHistory(true);
        const normalizedPostId = postId === undefined || postId === null || postId === "undefined" ? undefined : postId;
        const history = await chatService.getChatHistory(contentPlanId, normalizedPostId);
        
        if (connectionAttemptRef.current === currentAttempt && chatServiceRef.current === chatService) {
          setMessages(history);
        }
      } catch (historyError) {
        // Не критично, главное что подключение восстановлено
      } finally {
        if (connectionAttemptRef.current === currentAttempt) {
          setIsLoadingHistory(false);
        }
      }

      return true;

    } catch (error) {
      if (connectionAttemptRef.current === connectionAttemptRef.current) {
        if (error instanceof Error) {
          if (error.message.includes('Failed to fetch') || 
              error.message.includes('ERR_CONNECTION_RESET') ||
              error.message.includes('ERR_NETWORK')) {
            setError('Нет подключения к интернету или сервер недоступен');
          } else {
            setError('Не удалось переподключиться к чату');
          }
        } else {
          setError('Ошибка переподключения');
        }
        setIsConnected(false);
      }
      
      return false;
    } finally {
      initializingRef.current = false;
      setIsLoading(false);
    }
  }, [contentPlanId, postId]);

  return {
    // Состояние
    messages,
    isTyping,
    chatStatus,
    isConnected,
    isLoading,
    isLoadingHistory,
    error,
    
    // Действия
    sendMessage,
    regenerateMessage,
    sendAudioMessage,
    deleteMessage,
    clearHistory,
    clearError,
    updateMessageContent,
    updatePostIdInMessages,
    reconnect,
    
    // Вспомогательные данные
    messagesCount: messages.length,
    hasMessages: messages.length > 0
  };
}; 